import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/context";
import type { User } from "../auth/api";
import { ApiKeysSection } from "../components/ApiKeysSection";

// /settings — claim or change your @handle and display name.  Other account
// settings (push, sign-out, install) live elsewhere; this page is intended
// to grow as we add contacts/notifications/privacy controls.

export function Settings() {
  const { state, setUser } = useAuth();
  const user =
    state.status === "authenticated" || state.status === "offline"
      ? state.user
      : null;
  const accessToken =
    (state.status === "authenticated" && state.session.access_token) ||
    (state.status === "offline" && state.session.access_token) ||
    null;

  const [handle, setHandle] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Seed the form when the user object first arrives (or changes).
  useEffect(() => {
    if (!user) return;
    setHandle(user.handle ?? "");
    setDisplayName(user.display_name ?? "");
  }, [user?.handle, user?.display_name]);

  if (state.status === "loading") {
    return (
      <main className="mx-auto max-w-2xl px-6 py-10 text-sm text-slate-500 dark:text-slate-400">
        Loading…
      </main>
    );
  }
  if (!user) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-10 text-sm text-slate-500 dark:text-slate-400">
        You need to be signed in to view settings.{" "}
        <Link to="/signin" className="underline">
          Sign in
        </Link>
      </main>
    );
  }

  const handleClean = handle.trim().replace(/^@/, "");
  const handleDirty = handleClean !== (user.handle ?? "");
  const displayDirty =
    displayName.trim() !== (user.display_name ?? "").trim();
  const handleValid =
    handleClean === "" || /^[a-z0-9_]{3,20}$/.test(handleClean);
  const canSave =
    (handleDirty || displayDirty) && handleValid && !saving && !!accessToken;

  const save = async () => {
    if (!accessToken) return;
    setSaving(true);
    setError(null);
    try {
      const body: Record<string, string | null> = {};
      if (handleDirty) body.handle = handleClean === "" ? null : handleClean;
      if (displayDirty)
        body.display_name = displayName.trim() === "" ? null : displayName.trim();
      const r = await fetch("/api/auth/me", {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(body),
      });
      const data = (await r.json().catch(() => null)) as
        | (User & { error?: string })
        | { error: string }
        | null;
      if (!r.ok) {
        const code = data && "error" in data ? data.error : `HTTP ${r.status}`;
        setError(code ?? "save_failed");
        return;
      }
      await setUser(data as User);
      setSavedAt(Date.now());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="mx-auto max-w-2xl px-6 py-8">
      <div className="mb-6 flex items-baseline justify-between">
        <h1 className="text-xl font-semibold tracking-tight">Settings</h1>
        <Link
          to="/"
          className="text-sm text-slate-500 underline hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          Back
        </Link>
      </div>

      <section className="space-y-4 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
        <div>
          <label className="block text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Email
          </label>
          <div className="mt-1 text-sm text-slate-800 dark:text-slate-100">{user.email}</div>
        </div>

        <div>
          <label
            htmlFor="handle"
            className="block text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
          >
            Your handle
          </label>
          <div className="mt-1 flex items-center gap-2">
            <span className="select-none text-slate-400 dark:text-slate-500">@</span>
            <input
              id="handle"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="e.g. alice"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="flex-1 rounded border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-800"
            />
          </div>
          <p
            className={`mt-1 text-[11px] ${
              handleClean !== "" && !handleValid
                ? "text-rose-600 dark:text-rose-400"
                : "text-slate-500 dark:text-slate-400"
            }`}
          >
            Lowercase letters, digits, underscores. 3–20 characters. Leave blank
            to clear.
          </p>
        </div>

        <div>
          <label
            htmlFor="display_name"
            className="block text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
          >
            Display name
          </label>
          <input
            id="display_name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="How you'd like to appear to contacts"
            className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-800"
          />
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => void save()}
            disabled={!canSave}
            className="rounded bg-slate-900 px-3 py-1 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
          {error && (
            <span className="text-xs text-rose-600 dark:text-rose-400">{errorMessage(error)}</span>
          )}
          {!error && savedAt && Date.now() - savedAt < 4000 && (
            <span className="text-xs text-emerald-600 dark:text-emerald-400">Saved</span>
          )}
        </div>
      </section>

      {accessToken && <ApiKeysSection accessToken={accessToken} />}
    </main>
  );
}

function errorMessage(code: string): string {
  switch (code) {
    case "handle_taken":
      return "That handle is already taken.";
    case "handle_reserved":
      return "That handle is reserved.";
    case "invalid_body":
      return "Handle must be lowercase letters, digits, or underscores (3–20 chars).";
    default:
      return code;
  }
}
